/**
 * PTEL Andalucía - OCR Error Corrector (Fusionado)
 *
 * Corrige errores típicos de reconocimiento óptico de caracteres (OCR)
 * en coordenadas de documentos PTEL escaneados.
 *
 * Errores OCR frecuentes (30-40% documentos escaneados):
 * - 0 ↔ O/o (cero vs letra O)
 * - 1 ↔ l ↔ I/i (uno vs ele minúscula vs i)
 * - 5 ↔ S/s (cinco vs ese)
 * - 6 ↔ G/g (seis vs ge) — solo entre dígitos
 * - 8 ↔ B/b (ocho vs be)
 * - ° ↔ o (símbolo grado en contexto DMS)
 * - espacios internos en coordenadas UTM
 *
 * Estrategia: detección de secuencias «coordinables» + corrección DMS
 * separada + eliminación de espacios internos en números.
 *
 * @version 2.0.0
 * @date 2026-02-08
 * @see QA-01, F2.01
 */

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export interface OCRCorrectionResult {
  /** Texto corregido */
  corrected: string;
  /** Si se modificó el texto */
  wasModified: boolean;
  /** Correcciones aplicadas con detalle */
  corrections: OCRCorrection[];
  /** Confianza de la corrección (0-100) */
  confidence: number;
  /** Razón de early-exit si aplica */
  earlyExitReason?: 'NO_LETTERS' | 'EMPTY' | 'CLEAN';
}

export interface OCRCorrection {
  /** Posición en el string original */
  position: number;
  /** Carácter original (corrupto) */
  original: string;
  /** Carácter corregido */
  corrected: string;
  /** Tipo de error OCR */
  errorType: OCRErrorType;
  /** Contexto (caracteres alrededor) */
  context: string;
}

export type OCRErrorType =
  | 'ZERO_O'        // 0 ↔ O/o
  | 'ONE_L_I'       // 1 ↔ l ↔ I/i
  | 'FIVE_S'        // 5 ↔ S/s
  | 'SIX_G'         // 6 ↔ G/g (solo entre dígitos)
  | 'EIGHT_B'       // 8 ↔ B/b
  | 'DEGREE_O'      // ° ↔ o (en contexto DMS)
  | 'SPACE_NUMBER'  // espacio dentro de número
  | 'UNKNOWN';

// ============================================================================
// MAPA DE SUSTITUCIONES
// ============================================================================

/** Caracteres que OCR confunde con dígitos */
const DIGIT_SUBS: ReadonlyMap<string, { digit: string; errorType: OCRErrorType }> = new Map([
  ['O', { digit: '0', errorType: 'ZERO_O' }],
  ['o', { digit: '0', errorType: 'ZERO_O' }],
  ['l', { digit: '1', errorType: 'ONE_L_I' }],
  ['I', { digit: '1', errorType: 'ONE_L_I' }],
  ['i', { digit: '1', errorType: 'ONE_L_I' }],
  ['S', { digit: '5', errorType: 'FIVE_S' }],
  ['s', { digit: '5', errorType: 'FIVE_S' }],
  ['B', { digit: '8', errorType: 'EIGHT_B' }],
  ['b', { digit: '6', errorType: 'SIX_G' }],
]);

/** G/g → 6 pero SOLO entre dígitos (caso especial) */
const G_SUB = { digit: '6', errorType: 'SIX_G' as OCRErrorType };

// ============================================================================
// DETECTORES
// ============================================================================

/** Letras confundibles con dígitos (detector rápido) */
const OCR_SUSPECT = /[OolIiSsBbGg]/;

/**
 * Contexto numérico: ≥3 dígitos consecutivos, o dígito+confusable+dígito,
 * o patrón DMS con o minúscula (incl. dígitos OCR tras la o),
 * o heurística 0-como-grado ({2dig}0{espacio}{dig}).
 */
const COORD_CONTEXT = /\d{3,}|\d{1,3}o\d|\d{1,3}o[OolIi]|\d[OolIiSsBbGg]\d/;

/** Heurística DMS: dígitos + 0 + espacio + minutos + comilla + NSEW */
const DMS_ZERO_CONTEXT = /\d{2}0\s+\d{1,2}['′][NnSsEeWw]/;

// ============================================================================
// PATRONES DE CORRECCIÓN
// ============================================================================

/**
 * Secuencias «coordinables»: ≥4 chars de dígitos + letras confundibles,
 * sin letras normales adyacentes. Debe tener ≥2 dígitos reales.
 */
const COORDINABLE = /(?<![a-zA-Z])([0-9OolIiSsBbGg]{4,})(?![a-zA-Z])/g;

/**
 * Símbolo grado OCR: dígitos + o + (dígitos o letras OCR-confundibles) + comilla.
 * Acepta I/l/O en minutos porque el pipeline de grado corre antes que la corrección de dígitos.
 */
const DEGREE_O = /(\d{1,3})o([0-9OolIi]{1,2})(['′])/g;

/**
 * Heurística DMS: {2dígitos}0{espacio}{1-2dígitos}{comilla}{NSEW}
 * Detecta "370 12'N" como "37° 12'N" (0 ambiguo como símbolo grado).
 */
const DEGREE_ZERO = /(\d{2})0(\s+\d{1,2})(['′][NnSsEeWw])/g;

/** Componentes DMS cortos entre delimitadores (°, ', ") */
const DMS_COMPONENT = /(?<=[°'′"″\s])([0-9OolIiSsBbGg]{1,3})(?=['′"″NnSsEeWwOo\s])/g;

/** Espacios internos en números largos (≥6 dígitos totales) */
const SPACED_NUMBER = /(?<![a-zA-Z])(\d{2,})\s+(\d{3,})(?![a-zA-Z])/g;

// ============================================================================
// CORRECTORES INTERNOS
// ============================================================================

function getContext(text: string, pos: number, radius = 5): string {
  return text.slice(Math.max(0, pos - radius), Math.min(text.length, pos + radius + 1));
}

/**
 * Sustituye un carácter confundible, con tratamiento especial para G/g
 * (solo se corrige cuando está entre dígitos reales).
 */
function substituteChar(
  ch: string,
  prevChar: string | undefined,
  nextChar: string | undefined
): { digit: string; errorType: OCRErrorType } | null {
  // G/g: solo entre dígitos
  if (ch === 'G' || ch === 'g') {
    const prevIsDigit = prevChar !== undefined && /\d/.test(prevChar);
    const nextIsDigit = nextChar !== undefined && /\d/.test(nextChar);
    return prevIsDigit && nextIsDigit ? G_SUB : null;
  }
  return DIGIT_SUBS.get(ch) ?? null;
}

/** Corrige o→° en contexto DMS y también letras OCR en minutos adyacentes */
function fixDegreeSymbol(text: string, corrections: OCRCorrection[]): string {
  // Paso A: o minúscula → ° (acepta OCR-corrupted minutes)
  let result = text.replace(DEGREE_O, (match, degrees: string, mins: string, quote: string, offset: number) => {
    const oPos = offset + degrees.length;
    corrections.push({
      position: oPos,
      original: 'o',
      corrected: '°',
      errorType: 'DEGREE_O',
      context: getContext(text, oPos),
    });
    // También corregir OCR en los minutos (I→1, l→1, O→0)
    let fixedMins = '';
    for (let i = 0; i < mins.length; i++) {
      const ch = mins[i];
      const sub = DIGIT_SUBS.get(ch);
      if (sub) {
        const absPos = oPos + 1 + i;
        corrections.push({
          position: absPos,
          original: ch,
          corrected: sub.digit,
          errorType: sub.errorType,
          context: getContext(text, absPos),
        });
        fixedMins += sub.digit;
      } else {
        fixedMins += ch;
      }
    }
    return `${degrees}°${fixedMins}${quote}`;
  });

  // Paso B: Heurística "370 12'N" → "37° 12'N" (0 ambiguo como grado)
  result = result.replace(DEGREE_ZERO, (match, degrees: string, rest: string, suffix: string, offset: number) => {
    const zeroPos = offset + degrees.length;
    corrections.push({
      position: zeroPos,
      original: '0',
      corrected: '°',
      errorType: 'DEGREE_O',
      context: getContext(result, zeroPos),
    });
    return `${degrees}°${rest}${suffix}`;
  });

  return result;
}

/** Corrige confusiones en componentes DMS cortos entre delimitadores */
function fixDmsComponents(text: string, corrections: OCRCorrection[]): string {
  if (!/[°'′"″]/.test(text)) return text;

  return text.replace(DMS_COMPONENT, (match, _g, offset: number) => {
    if ((match.match(/[0-9]/g) || []).length < 1) return match;
    if (!OCR_SUSPECT.test(match)) return match;

    let corrected = '';
    for (let i = 0; i < match.length; i++) {
      const ch = match[i];
      const sub = substituteChar(ch, match[i - 1], match[i + 1]);
      if (sub) {
        corrections.push({
          position: offset + i,
          original: ch,
          corrected: sub.digit,
          errorType: sub.errorType,
          context: getContext(text, offset + i),
        });
        corrected += sub.digit;
      } else {
        corrected += ch;
      }
    }
    return corrected;
  });
}

/** Corrige confusiones dígito↔letra en secuencias «coordinables» largas */
function fixDigitConfusions(text: string, corrections: OCRCorrection[]): string {
  return text.replace(COORDINABLE, (match, _g, offset: number) => {
    const realDigits = (match.match(/[0-9]/g) || []).length;
    if (realDigits < 2) return match;
    if (!OCR_SUSPECT.test(match)) return match;

    let corrected = '';
    for (let i = 0; i < match.length; i++) {
      const ch = match[i];
      const sub = substituteChar(ch, match[i - 1], match[i + 1]);
      if (sub) {
        corrections.push({
          position: offset + i,
          original: ch,
          corrected: sub.digit,
          errorType: sub.errorType,
          context: getContext(text, offset + i),
        });
        corrected += sub.digit;
      } else {
        corrected += ch;
      }
    }
    return corrected;
  });
}

/** Elimina espacios internos en secuencias numéricas ≥6 dígitos */
function fixInternalSpaces(text: string, corrections: OCRCorrection[]): string {
  return text.replace(SPACED_NUMBER, (match, g1: string, g2: string, offset: number) => {
    if (g1.length + g2.length < 6) return match;
    const spacePos = offset + g1.length;
    corrections.push({
      position: spacePos,
      original: ' ',
      corrected: '',
      errorType: 'SPACE_NUMBER',
      context: getContext(text, spacePos),
    });
    return `${g1}${g2}`;
  });
}

// ============================================================================
// CLASE PRINCIPAL
// ============================================================================

export class OCRCorrector {
  private totalProcessed = 0;
  private totalCorrected = 0;
  private correctionsByType: Record<OCRErrorType, number> = {
    ZERO_O: 0, ONE_L_I: 0, FIVE_S: 0, SIX_G: 0,
    EIGHT_B: 0, DEGREE_O: 0, SPACE_NUMBER: 0, UNKNOWN: 0,
  };

  /**
   * Corrige errores OCR en texto de coordenada.
   * Pipeline: grado → DMS → coordinables → espacios
   */
  correct(text: string): OCRCorrectionResult {
    this.totalProcessed++;

    if (!text || text.length === 0) {
      return this.buildResult(text, text, [], 100, 'EMPTY');
    }

    // Early-exit 1: no tiene letras sospechosas Y no es heurística 0-como-grado
    if (!OCR_SUSPECT.test(text) && !DMS_ZERO_CONTEXT.test(text)) {
      return this.buildResult(text, text, [], 100, 'NO_LETTERS');
    }

    // Early-exit 2: no parece coordenada (sin contexto numérico ni DMS-zero)
    if (!COORD_CONTEXT.test(text) && !DMS_ZERO_CONTEXT.test(text)) {
      return this.buildResult(text, text, [], 100, 'CLEAN');
    }

    const corrections: OCRCorrection[] = [];
    let result = text;

    // Paso 1: Símbolo grado (o → ° en DMS con comilla)
    result = fixDegreeSymbol(result, corrections);

    // Paso 2: Componentes DMS cortos
    result = fixDmsComponents(result, corrections);

    // Paso 3: Secuencias coordinables largas
    result = fixDigitConfusions(result, corrections);

    // Paso 4: Espacios internos en UTM
    result = fixInternalSpaces(result, corrections);

    if (result !== text) {
      this.totalCorrected++;
      for (const c of corrections) {
        this.correctionsByType[c.errorType]++;
      }
    }

    const confidence = this.calculateConfidence(corrections.length, text.length);
    return this.buildResult(text, result, corrections, confidence);
  }

  private calculateConfidence(numCorrections: number, textLength: number): number {
    if (numCorrections === 0) return 100;
    if (textLength === 0) return 0;
    const ratio = numCorrections / textLength;
    if (ratio > 0.3) return 40;
    if (ratio > 0.2) return 60;
    if (ratio > 0.1) return 80;
    return 95;
  }

  private buildResult(
    original: string,
    corrected: string,
    corrections: OCRCorrection[],
    confidence: number,
    earlyExitReason?: OCRCorrectionResult['earlyExitReason']
  ): OCRCorrectionResult {
    return {
      corrected,
      wasModified: original !== corrected,
      corrections,
      confidence,
      earlyExitReason,
    };
  }

  getStats() {
    return {
      totalProcessed: this.totalProcessed,
      totalCorrected: this.totalCorrected,
      correctionsByType: { ...this.correctionsByType },
      correctionRate: this.totalProcessed > 0
        ? (this.totalCorrected / this.totalProcessed) * 100
        : 0,
    };
  }

  resetStats(): void {
    this.totalProcessed = 0;
    this.totalCorrected = 0;
    for (const key of Object.keys(this.correctionsByType) as OCRErrorType[]) {
      this.correctionsByType[key] = 0;
    }
  }
}

// ============================================================================
// SINGLETON & EXPORTS
// ============================================================================

export const ocrCorrector = new OCRCorrector();

/** Corrección completa con metadatos (API pipeline) */
export function correctOCR(text: string): OCRCorrectionResult {
  return ocrCorrector.correct(text);
}

/** Solo texto corregido */
export function fixOCR(text: string): string {
  return ocrCorrector.correct(text).corrected;
}

/** Solo texto corregido (alias para compatibilidad) */
export function correctOcrSimple(text: string): string {
  return ocrCorrector.correct(text).corrected;
}

/** Detección rápida: ¿tiene letras OCR sospechosas en contexto numérico? */
export function hasOCRErrors(text: string): boolean {
  return OCR_SUSPECT.test(text) && COORD_CONTEXT.test(text);
}
